import { useState, useEffect, useCallback } from 'react'
import { ping } from '@/api/avpulse-api'

export const usePing = () => {
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [worker, setWorker] = useState<Worker | null>(null)
  const [pingStatus, setPingStatus] = useState<{
    isWorking: boolean
    lastSuccess: string | null
    lastError: string | null
  }>({
    isWorking: false,
    lastSuccess: null,
    lastError: null,
  })

  // Initialize ping worker (runs regardless of authentication)
  useEffect(() => {
    const pingWorker = new Worker(
      new URL('../workers/pingWorker.ts', import.meta.url),
      { type: 'module' },
    )

    // Handle ping messages
    pingWorker.onmessage = e => {
      const { type, success, error: errorMessage, timestamp } = e.data
      if (type === 'ping') {
        setPingStatus(prev => ({
          isWorking: true,
          lastSuccess: success ? timestamp : prev.lastSuccess,
          lastError: success ? null : errorMessage?.message || 'Unknown error',
        }))
      }
    }

    setWorker(pingWorker)

    // Cleanup on unmount
    return () => {
      pingWorker.terminate()
    }
  }, [])

  // Manual ping function (can be called anytime)
  const checkPing = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      const data = await ping()
      console.log('Manual ping successful:', data)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Ping failed'
      setError(errorMessage)
      console.error('Manual ping error:', errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    checkPing,
    error,
    isLoading,
    pingStatus,
  }
}
